import React from 'react';
import { connect } from 'react-redux';
import { Button, Modal, FormGroup, ControlLabel, FormControl, HelpBlock, Alert } from 'react-bootstrap';
import { translate } from 'react-i18next';
import ProjectDetail from '../../components/project_detail/ProjectDetail';
import { createProjectFollowup } from '../../redux/projects/projects_actions';
import { listEvaluationPurpose } from '../../redux/references/evaluation_purpose_actions';
import * as RestClient from '../../utils/rest-service';
import Constants from '../../config/constants';

const FORCED_EP_KEYWORD = 'อ.5';

class ProjectCreateReferenceModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'search',
      projectId: '',
      referenceProject: null,
      isFetching: false,
      fetchError: null,
    };
  }

  componentDidMount() {
    this.props.listEvaluationPurpose();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isCreating !== this.props.isCreating && !nextProps.isCreating) {
      if (!nextProps.errorMessage) {
        this.props.onHide();
      }
    }
  }

  getForcedEvaluationPurpose() {
    const eps = this.props.evaluationPurposes || [];
    const match = eps.find(ep => (ep.name_th || ep.name || '').includes(FORCED_EP_KEYWORD));
    return match ? (match.name_th || match.name) : null;
  }

  sanitizeProject(project) {
    if (!project) return project;
    const stringFields = ['name', 'owner', 'designer', 'address', 'description', 'issuer'];
    const result = { ...project };
    stringFields.forEach(key => {
      if (result[key] == null) {
        result[key] = '';
      }
    });
    return result;
  }

  onSearchHandler() {
    const { projectId } = this.state;
    if (!projectId || !projectId.trim()) return;

    this.setState({ isFetching: true, fetchError: null, referenceProject: null });

    RestClient.request({
      method: 'get',
      url: Constants.API_PROJECT_REFERENCE(projectId.trim()),
    })
      .then(data => {
        this.setState({
          isFetching: false,
          referenceProject: data,
          step: 'preview',
        });
      })
      .catch(err => {
        const msg =
          err && err.detail
            ? err.detail
            : this.props.t('reference project not found');
        this.setState({ isFetching: false, fetchError: msg });
      });
  }

  onCreateHandler() {
    const { referenceProject } = this.state;
    if (!referenceProject || referenceProject.id == null) return;

    const forcedEP = this.getForcedEvaluationPurpose();
    const evaluationPurpose =
      (forcedEP != null && String(forcedEP).trim() !== '')
        ? String(forcedEP).trim()
        : (referenceProject.evaluation_purpose != null
          ? String(referenceProject.evaluation_purpose).trim()
          : '');
    this.props.createProjectFollowup(referenceProject.id, {
      evaluation_purpose: evaluationPurpose,
    });
  }

  onHideHandler() {
    this.setState({
      step: 'search',
      projectId: '',
      referenceProject: null,
      isFetching: false,
      fetchError: null,
    });
    this.props.onHide();
  }

  renderSearchStep() {
    const { t } = this.props;
    const { projectId, isFetching, fetchError } = this.state;

    return (
      <div>
        <FormGroup validationState={fetchError ? 'error' : null}>
          <ControlLabel>{t('reference project id')}</ControlLabel>
          <div style={{ display: 'flex', gap: 8 }}>
            <FormControl
              id="reference-project-id-input"
              type="text"
              value={projectId}
              placeholder={t('enter reference project id')}
              onChange={e => this.setState({ projectId: e.target.value, fetchError: null })}
              onKeyDown={e => {
                if (e.key === 'Enter') this.onSearchHandler();
              }}
              disabled={isFetching}
              style={{ flex: 1 }}
            />
            <Button
              id="reference-project-search"
              bsStyle="primary"
              disabled={isFetching || !projectId.trim()}
              onClick={() => this.onSearchHandler()}
              style={{ flexShrink: 0 }}
            >
              {isFetching
                ? <span><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('fetching')}...</span>
                : <span><i className="fas fa-search" style={{ marginRight: 6 }} />{t('search')}</span>}
            </Button>
          </div>
          {fetchError && <HelpBlock style={{ color: '#a94442' }}>{fetchError}</HelpBlock>}
        </FormGroup>
      </div>
    );
  }

  renderPreviewStep() {
    const { referenceProject } = this.state;
    if (!referenceProject) return null;

    const { t } = this.props;
    const forcedEP = this.getForcedEvaluationPurpose();
    const previewProject = this.sanitizeProject(
      forcedEP
        ? { ...referenceProject, evaluation_purpose: forcedEP }
        : referenceProject,
    );

    return (
      <div>
        <Alert bsStyle="info" style={{ marginBottom: 8 }}>
          <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
          {t('reference project preview notice')}
        </Alert>
        {forcedEP && (
          <Alert bsStyle="warning" style={{ marginBottom: 16 }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
            {t('reference ep forced notice')}{' '}
            <strong>{forcedEP}</strong>
          </Alert>
        )}
        <ProjectDetail
          project={previewProject}
          disabled={true}
        />
      </div>
    );
  }

  render() {
    const { isCreating, errorMessage, createProjectFollowup: _cf, t, ...newProps } = this.props;
    const { step, isFetching } = this.state;

    return (
      <Modal
        {...newProps}
        bsSize="large"
        aria-labelledby="contained-modal-title-ref"
        backdrop="static"
        onHide={() => this.onHideHandler()}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-ref">
            {t('create project reference')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step === 'search' && this.renderSearchStep()}
          {step === 'preview' && this.renderPreviewStep()}
          {errorMessage && (
            <Alert bsStyle="danger" style={{ marginTop: 12 }}>
              {typeof errorMessage === 'string'
                ? errorMessage
                : JSON.stringify(errorMessage)}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {step === 'search' && (
            <Button onClick={() => this.onHideHandler()}>
              {t('cancel') || 'Cancel'}
            </Button>
          )}
          {step === 'preview' && (
            <React.Fragment>
              <Button
                id="reference-project-back"
                onClick={() => this.setState({ step: 'search', referenceProject: null })}
                disabled={isCreating}
              >
                {t('back')}
              </Button>
              <Button
                id="reference-project-create"
                bsStyle="primary"
                disabled={isCreating}
                onClick={() => this.onCreateHandler()}
              >
                {isCreating
                  ? `${t('creating')}...`
                  : t('create')}
              </Button>
            </React.Fragment>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  isCreating: state.project.creating,
  errorMessage: state.project.errorMessage,
  evaluationPurposes: state.reference.evaluationPurpose.evaluationPurposes,
});

const mapDispatchToProps = dispatch => ({
  createProjectFollowup: (sourceId, options) =>
    dispatch(createProjectFollowup(sourceId, options)),
  listEvaluationPurpose: () => dispatch(listEvaluationPurpose()),
});

export default translate('translations')(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { withRef: true },
  )(ProjectCreateReferenceModal),
);



// WEBPACK FOOTER //
// ./src/modal/projects/ProjectCreateReferenceModal.js